/**
 * widgetCatalog — Stream D (Paritaet R4)
 *
 * Gemeinsame, React-freie Basis fuer den Dashboard-Widget-Katalog.
 * Enthaelt die Widget-IDs (12 v1-Typen aus /dashboard_boxes + die 4 v2-Karten),
 * das Default-Set (= effektive Liste fuer Free-User, identisch zur BE-Konstante
 * WidgetCatalog::DEFAULT_IDS) und die tolerante Layout-Migration.
 *
 * Warum eine eigene Datei: useDashboardLayout (React-Hook) und
 * useDashboardWidgets (Server-Katalog) brauchen dieselben Konstanten —
 * ein gemeinsames Modul vermeidet einen Zyklus zwischen beiden Hooks.
 */

/**
 * Die v1-Kacheln aus dem Monolithen (dashboard_boxes).
 *
 * OHNE 'graph': die v1-Kachel "Diagramm" und die v2-Karte 'rewards_chart'
 * rendern denselben Rewards-Chart. Stuenden beide im Katalog, koennte ein
 * Layout beide sichtbar haben und das Dashboard zeigte den Chart doppelt
 * (R4-Review F7). 'graph' lebt deshalb nur noch als LEGACY-ALIAS weiter und
 * wird beim Laden auf 'rewards_chart' abgebildet — siehe resolveWidgetId.
 */
export const V1_WIDGET_IDS = [
  'rewards_7d',
  'avg_7d',
  'avg_30d',
  'rewards_this_month',
  'rewards_yesterday',
  'rewards_30d',
  'balance',
  'rewards_last_month',
  'hotspots',
  'since_payout',
  'rewards_two_days_ago',
] as const

/** Die 4 v2-Karten, die es in v1 nicht gibt. */
export const V2_WIDGET_IDS = [
  'kpi_strip',
  'rewards_chart',
  'top_hotspots',
  'network_health',
] as const

export const ALL_WIDGET_IDS = [...V1_WIDGET_IDS, ...V2_WIDGET_IDS] as const

export type WidgetId = (typeof ALL_WIDGET_IDS)[number]

/**
 * Default-Set laut BE-Contract. Fuer Free-User ist das gleichzeitig die
 * effektive Liste (Customize ist Premium, v1-Paritaet).
 */
export const DEFAULT_WIDGET_IDS: WidgetId[] = [
  'rewards_7d',
  'avg_7d',
  'rewards_yesterday',
  'rewards_30d',
  'balance',
  'kpi_strip',
  'rewards_chart',
  'top_hotspots',
  'network_health',
]

/** i18n-Keys (Namespace `dashboard`) — nie hartkodierte Labels. */
export const WIDGET_LABEL_KEYS: Record<WidgetId, string> = {
  rewards_7d: 'customizer.widgets.rewards7d',
  avg_7d: 'customizer.widgets.avg7d',
  avg_30d: 'customizer.widgets.avg30d',
  rewards_this_month: 'customizer.widgets.rewardsThisMonth',
  rewards_yesterday: 'customizer.widgets.rewardsYesterday',
  rewards_30d: 'customizer.widgets.rewards30d',
  balance: 'customizer.widgets.balance',
  rewards_last_month: 'customizer.widgets.rewardsLastMonth',
  hotspots: 'customizer.widgets.hotspots',
  since_payout: 'customizer.widgets.sincePayout',
  rewards_two_days_ago: 'customizer.widgets.rewardsTwoDaysAgo',
  kpi_strip: 'customizer.widgets.kpiStrip',
  rewards_chart: 'customizer.widgets.rewardsChart',
  top_hotspots: 'customizer.widgets.topHotspots',
  network_health: 'customizer.widgets.networkHealth',
}

export type LayoutEntry = { id: WidgetId; visible: boolean }

/**
 * Grid-Groesse je Widget (Vorlage dashboard.html .widget[data-size]):
 * alle v1-Kacheln sind 'tile' (3/12), die 4 v2-Karten haben individuelle
 * Groessen. Bestimmt NUR die CSS-Grid-Spanne in .dash-grid, keine Logik.
 */
export const WIDGET_GRID_SIZE: Record<WidgetId, 'tile' | 'half' | 'wide' | 'third' | 'full'> = {
  rewards_7d: 'tile',
  avg_7d: 'tile',
  avg_30d: 'tile',
  rewards_this_month: 'tile',
  rewards_yesterday: 'tile',
  rewards_30d: 'tile',
  balance: 'tile',
  rewards_last_month: 'tile',
  // Enthaelt die volle Hotspot-Tabelle (HotspotsWidgetCard), keine reine
  // Stat-Kachel wie in der Vorlage -> braucht mehr Breite als 3/12.
  hotspots: 'wide',
  since_payout: 'tile',
  rewards_two_days_ago: 'tile',
  kpi_strip: 'full',
  rewards_chart: 'wide',
  top_hotspots: 'third',
  network_health: 'half',
}

export function isWidgetId(value: unknown): value is WidgetId {
  return typeof value === 'string' && (ALL_WIDGET_IDS as readonly string[]).includes(value)
}

/**
 * Legacy-IDs, die auf eine aktuelle Kachel abgebildet werden (R4-Review F7).
 * 'graph' (v1 "Diagramm") und 'rewards_chart' sind derselbe Chart — gespeicherte
 * 'graph'-Eintraege (localStorage oder users.dashboard_layout) werden beim Laden
 * auf 'rewards_chart' gehoben und dedupliziert, damit der Chart nie doppelt
 * gerendert wird.
 */
export const LEGACY_WIDGET_ALIASES: Record<string, WidgetId> = {
  graph: 'rewards_chart',
}

/** Normalisiert eine (ggf. veraltete) Widget-ID; null wenn unbekannt. */
export function resolveWidgetId(value: unknown): WidgetId | null {
  if (typeof value !== 'string') return null
  const alias = LEGACY_WIDGET_ALIASES[value]
  if (alias) return alias
  return isWidgetId(value) ? value : null
}

/** Normalisiert + dedupliziert eine Liste von Widget-IDs (Reihenfolge bleibt). */
export function resolveWidgetIds(raw: unknown): WidgetId[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<WidgetId>()
  const out: WidgetId[] = []
  for (const item of raw) {
    const id = resolveWidgetId(item)
    if (id === null || seen.has(id)) continue
    seen.add(id)
    out.push(id)
  }
  return out
}

/**
 * Tolerante Migration eines gespeicherten Layouts (localStorage
 * `ht_dashboard_layout` oder users.dashboard_layout).
 *
 * Regeln:
 *  - unbekannte IDs (z. B. aus einer aelteren/neueren Version) werden verworfen,
 *  - Legacy-Aliase werden gehoben ('graph' -> 'rewards_chart', R4-Review F7),
 *  - Duplikate werden entfernt (erster Treffer gewinnt; ein Layout mit BEIDEN
 *    Chart-IDs behaelt genau einen Eintrag — sichtbar, wenn eine der beiden
 *    Quellen sichtbar war),
 *  - fehlende Widgets werden hinten AUSGEBLENDET angehaengt: sie tauchen im
 *    Customizer auf, veraendern aber kein bestehendes Dashboard. Wichtig fuer
 *    die Deckungsgleichheit mit der serverseitigen `effective`-Liste — der
 *    Server kennt nur die gespeicherten Eintraege.
 *  - leeres/ungueltiges Layout => komplettes Default-Layout.
 */
export function migrateLayoutEntries(raw: unknown): LayoutEntry[] {
  const source = Array.isArray(raw) ? raw : []
  const seen = new Set<WidgetId>()
  const entries: LayoutEntry[] = []

  // IDs, die ueber einen Legacy-Alias hereinkamen ('graph' -> 'rewards_chart').
  const viaAlias = new Set<WidgetId>()

  for (const item of source) {
    if (!item || typeof item !== 'object') continue
    const rawId = (item as { id?: unknown }).id
    const id = resolveWidgetId(rawId)
    if (id === null) continue
    const aliased = rawId !== id
    const visible = (item as { visible?: unknown }).visible !== false

    if (seen.has(id)) {
      // Echte Duplikate derselben ID: erster Treffer gewinnt (unveraendert).
      // Nur bei einer ALIAS-Kollision ('graph' + 'rewards_chart' im selben
      // Layout) wird zusammengefuehrt: der eine verbleibende Eintrag ist
      // sichtbar, sobald eine der beiden Quellen sichtbar war (F7).
      if (visible && (aliased || viaAlias.has(id))) {
        const existing = entries.find((e) => e.id === id)
        if (existing) existing.visible = true
      }
      continue
    }

    seen.add(id)
    if (aliased) viaAlias.add(id)
    entries.push({ id, visible })
  }

  for (const id of ALL_WIDGET_IDS) {
    if (seen.has(id)) continue
    entries.push({ id, visible: false })
  }

  // Nichts Bekanntes im Input => sauberes Default-Layout aufbauen.
  if (seen.size === 0) return defaultLayoutEntries()

  return entries
}

export function defaultLayoutEntries(): LayoutEntry[] {
  const visible = DEFAULT_WIDGET_IDS.map((id) => ({ id, visible: true }))
  const hidden = ALL_WIDGET_IDS.filter((id) => !DEFAULT_WIDGET_IDS.includes(id)).map((id) => ({
    id,
    visible: false,
  }))
  return [...visible, ...hidden]
}
